import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, type UserRole } from '../services/AuthContext';
import { CircularProgress, Box, Typography } from '@mui/material';

interface PrivateRouteProps {
  element: React.ReactElement;
  role?: 'admin' | 'service' | 'technician' | 'viewer' | 'field_agent' | 'external_viewer' | 'warehouse';
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ element, role = 'viewer' }) => {
  const { isAuthenticated, loading, role: userRole } = useAuth();
  const currentPath = window.location.pathname;

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          טוען נתוני משתמש...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // אם המשתמש מחובר אבל התפקיד עוד לא נטען מהשרת (טוקן / Firestore),
  // נתייחס לזה כמצב טעינה נוסף במקום להעיף בחזרה לדף ההתחברות.
  if (!userRole) {
    console.warn('תפקיד המשתמש עדיין נטען – ממתין להרשאות מהשרת');
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          טוען הרשאות משתמש...
        </Typography>
      </Box>
    );
  }

  // רשימת תפקידים מורשים
  // former_employee בכוונה לא ברשימה — עזב את החברה
  const allowedRoles: Array<NonNullable<UserRole>> = ['admin', 'service', 'technician', 'viewer', 'field_agent', 'external_viewer', 'warehouse'];
  
  // בדוק שהתפקיד מורשה בכלל
  if (!allowedRoles.includes(userRole)) {
    console.log(`גישה חסומה – למשתמש תפקיד לא מורשה: ${userRole}`);
    return <Navigate to="/unauthorized" replace />;
  }

  // צופה חיצוני (בעל חנות שותף): דשבורד + קריאות בלבד
  if (userRole === 'external_viewer') {
    const externalAllowedPaths = ['/', '/dashboard', '/ServiceCallsPage'];
    if (!externalAllowedPaths.includes(currentPath)) {
      console.log(`גישה חסומה לצופה חיצוני: ${currentPath}`);
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // תפקיד מחסן: גישה רק לדשבורד, מאגר ידע והוצאת סחורה מהמלאי
  if (userRole === 'warehouse') {
    const warehouseAllowedPaths = [
      '/',
      '/dashboard',
      '/knowledge-base',
      '/inventory/outbound',
      '/inventory/outbound/history',
    ];
    const isKnowledgeBaseViewPath = /^\/knowledge-base\/[^/]+$/.test(currentPath);
    const isWarehouseAllowed =
      warehouseAllowedPaths.includes(currentPath) || isKnowledgeBaseViewPath;

    if (!isWarehouseAllowed) {
      console.log(`גישה חסומה למחסן: ${currentPath}`);
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // אם הוגדר תפקיד ספציפי (שאינו viewer), רק התפקיד עצמו או admin מורשים
  if (role !== 'viewer' && userRole !== role && userRole !== 'admin') {
    console.log(`גישה חסומה – נדרש ${role}, אך למשתמש תפקיד: ${userRole}`);
    return <Navigate to="/unauthorized" replace />;
  }

  // לכל התפקידים האחרים - אם הדף הוא role="viewer" (ברירת מחדל), כל התפקידים המורשים יכולים לגשת
  // אם הדף דורש תפקיד ספציפי אחר (לא admin), נבדוק בהתאם

  return element;
};

export default PrivateRoute;
